import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Eye, EyeOff, Copy, CheckCircle2 } from 'lucide-react';
import { pagesApi } from '../api/cms';
import { defaultPages } from '../api/defaults';
import type { PageContent } from '../types';
import { RichEditor, MediaPickerModal } from '../components/RichEditor';

const PAGE_NAMES: Record<string, string> = {
  home: 'Pagina Principală', webdesign: 'Web Design', callcenter: 'Call Center',
  hosting: 'Găzduire Web', voip: 'Telefonie VoIP', crm: 'CRM & Software',
  hardware: 'IT Hardware', contact: 'Contact', clienti: 'Clienți',
  termeni: 'Termeni & Condiții', gdpr: 'Politica GDPR', cookies: 'Politica Cookies',
  confidentialitate: 'Politică de Confidențialitate', retur: 'Politică de Retur',
  nda: 'Acord de Confidențialitate (NDA)', despre: 'Despre Noi',
};
const SITE_PATHS: Record<string, string> = {
  home: '/', webdesign: '/servicii/web-design', callcenter: '/servicii/call-center',
  hosting: '/servicii/gazduire', voip: '/servicii/telefonie-voip', crm: '/servicii/crm-software',
  hardware: '/it-hardware', contact: '/contact', clienti: '/clienti',
  termeni: '/termeni', gdpr: '/gdpr', cookies: '/cookies',
  confidentialitate: '/confidentialitate', retur: '/retur',
  nda: '/nda', despre: '/despre',
};

interface Section {
  id: string; label: string; roText: string; enText: string; imageUrl?: string;
}

export default function PageEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState<PageContent | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState<'ro' | 'en'>('ro');
  const [showPreview, setShowPreview] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showMedia, setShowMedia] = useState(false);
  const [mediaSectionId, setMediaSectionId] = useState<string | null>(null);
  // Track editor keys per section to force refresh on language switch
  const [editorKeys, setEditorKeys] = useState<Record<string, number>>({});

  useEffect(() => {
    pagesApi.get(id!).then(p => {
      if (p && p.sections && p.sections.length > 0) setPage(p);
      else {
        const def = defaultPages.find(d => d.pageId === id);
        setPage(def ? { ...def, id: id!, pageId: id! } : {
          id: id!, pageId: id!, pageName: PAGE_NAMES[id!] || id!,
          sections: [], updatedAt: new Date().toISOString().split('T')[0],
        });
      }
    }).catch(() => {
      const def = defaultPages.find(d => d.pageId === id);
      setPage(def ? { ...def, id: id!, pageId: id! } : {
        id: id!, pageId: id!, pageName: PAGE_NAMES[id!] || id!,
        sections: [], updatedAt: new Date().toISOString().split('T')[0],
      });
    });
  }, [id]);

  const updateSection = (sId: string, field: string, value: string) =>
    setPage(p => p ? { ...p, sections: p.sections.map(s => s.id === sId ? { ...s, [field]: value } : s) } : p);

  const addSection = () => {
    const newId = `section-${Date.now()}`;
    setPage(p => p ? { ...p, sections: [...p.sections, { id: newId, label: 'Secțiune nouă', roText: '', enText: '', imageUrl: '' }] } : p);
  };

  const removeSection = (sId: string) => {
    if (!confirm('Ștergi această secțiune?')) return;
    setPage(p => p ? { ...p, sections: p.sections.filter(s => s.id !== sId) } : p);
  };

  const duplicateSection = (section: Section) => {
    const newId = `section-${Date.now()}`;
    setPage(p => p ? { ...p, sections: [...p.sections, { ...section, id: newId, label: section.label + ' (copie)' }] } : p);
  };

  const moveSection = (idx: number, dir: -1 | 1) => {
    setPage(p => {
      if (!p) return p;
      const secs = [...p.sections];
      const target = idx + dir;
      if (target < 0 || target >= secs.length) return p;
      [secs[idx], secs[target]] = [secs[target], secs[idx]];
      return { ...p, sections: secs };
    });
  };

  const copyText = (text: string, key: string) => {
    navigator.clipboard.writeText(text.replace(/<[^>]*>/g, '')).then(() => {
      setCopiedId(key); setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const resetToDefault = () => {
    const def = defaultPages.find(d => d.pageId === id);
    if (!def) return;
    if (!confirm('Resetezi la valorile implicite? Modificările nesalvate se pierd.')) return;
    setPage({ ...def, id: id!, pageId: id! });
  };

  const handleMediaSelect = (url: string) => {
    if (mediaSectionId) {
      updateSection(mediaSectionId, 'imageUrl', url);
    }
    setShowMedia(false); setMediaSectionId(null);
  };

  const save = async () => {
    if (!page) return;
    setSaving(true);
    try {
      await pagesApi.update(page.id, page);
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch { alert('Eroare la salvare.'); } finally { setSaving(false); }
  };

  // When switching language tabs, bump all editor keys to reinit
  const handleTabSwitch = (lang: 'ro' | 'en') => {
    setActiveTab(lang);
    setEditorKeys(prev => {
      const next: Record<string, number> = {};
      page?.sections.forEach(s => { next[s.id] = (prev[s.id] || 0) + 1; });
      return next;
    });
  };

  const siteUrl = SITE_PATHS[id || ''];

  if (!page) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin w-6 h-6 border-2 border-[#4ab8ff] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate('/pages')} className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-all"><ArrowLeft size={18} /></button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-white">{page.pageName}</h1>
            {siteUrl && (
              <a href={`https://interpersonal.ro${siteUrl}`} target="_blank" rel="noopener noreferrer"
                className="text-xs text-[#4ab8ff] hover:underline opacity-60 hover:opacity-100">{siteUrl} ↗</a>
            )}
          </div>
          <p className="text-xs text-white/30 mt-0.5">{page.sections.length} secțiuni editabile</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={resetToDefault} className="px-3 py-1.5 rounded-lg text-xs text-white/40 hover:text-white hover:bg-white/5 border border-white/10 transition-all">Reset implicit</button>
          {siteUrl && (
            <button onClick={() => setShowPreview(!showPreview)}
              className={`p-2 rounded-lg border transition-all ${showPreview ? 'border-[#295CA8] text-[#4ab8ff] bg-[#295CA8]/10' : 'border-white/10 text-white/40 hover:text-white'}`}>
              {showPreview ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          )}
          {saved && <span className="text-xs text-green-400 bg-green-500/10 px-3 py-1.5 rounded-lg border border-green-500/20 flex items-center gap-1"><CheckCircle2 size={12} /> Salvat</span>}
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            <Save size={15} /> {saving ? 'Se salvează...' : 'Salvează'}
          </button>
        </div>
      </div>

      {/* Preview */}
      {showPreview && siteUrl && (
        <div className="bg-[#07090f] border border-white/10 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
            <span className="text-xs text-white/40">Preview live: {siteUrl}</span>
            <button onClick={() => setShowPreview(false)} className="text-xs text-white/30 hover:text-white">× Închide</button>
          </div>
          <iframe src={`https://interpersonal.ro${siteUrl}`} className="w-full border-0" style={{ height: 380 }} title="Preview" />
        </div>
      )}

      {/* Language tabs */}
      <div className="flex items-center gap-1 bg-white/5 p-1 rounded-lg w-fit">
        {(['ro', 'en'] as const).map(lang => (
          <button key={lang} onClick={() => handleTabSwitch(lang)}
            className={`px-4 py-1.5 rounded text-sm font-medium transition-all ${activeTab === lang ? 'bg-[#295CA8] text-white' : 'text-white/40 hover:text-white'}`}>
            {lang === 'ro' ? '🇷🇴 Română' : '🇬🇧 English'}
          </button>
        ))}
      </div>

      {/* Info */}
      <div className="flex items-start gap-3 p-3 rounded-lg text-xs" style={{ background: 'rgba(74,184,255,0.06)', border: '1px solid rgba(74,184,255,0.12)' }}>
        <span className="text-[#4ab8ff] text-base">ℹ</span>
        <div className="text-white/50">
          Editați textele paginii folosind editorul rich text (bold, titluri, liste, imagini din librărie).
          <strong className="text-white/70"> Textele actuale sunt pre-populate</strong> din conținutul real al paginii.
          Imaginile externe sunt descărcate automat în librăria locală la inserare.
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {page.sections.map((s, idx) => (
          <div key={s.id} className="bg-[#07090f] border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-all group">
            {/* Section header */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 bg-white/[0.015]">
              <div className="flex flex-col gap-0 opacity-30 group-hover:opacity-60 transition-opacity">
                <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="text-white/60 hover:text-white disabled:opacity-20 text-[10px] leading-tight">▲</button>
                <button onClick={() => moveSection(idx, 1)} disabled={idx === page.sections.length - 1} className="text-white/60 hover:text-white disabled:opacity-20 text-[10px] leading-tight">▼</button>
              </div>
              <span className="text-xs text-white/20 font-mono w-5">#{idx + 1}</span>
              <input value={s.label} onChange={e => updateSection(s.id, 'label', e.target.value)}
                className="flex-1 bg-transparent text-sm font-medium text-white/60 focus:outline-none focus:text-white hover:text-white transition-colors" />
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => copyText(activeTab === 'ro' ? s.roText : s.enText, s.id)}
                  className="p-1.5 rounded text-white/30 hover:text-[#4ab8ff] hover:bg-[#295CA8]/10 transition-all" title="Copiază text">
                  {copiedId === s.id ? <CheckCircle2 size={12} className="text-green-400" /> : <Copy size={12} />}
                </button>
                <button onClick={() => duplicateSection(s)} className="p-1.5 rounded text-white/30 hover:text-white hover:bg-white/5 transition-all" title="Duplică">
                  <Plus size={12} />
                </button>
                <button onClick={() => removeSection(s.id)} className="p-1.5 rounded text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-all" title="Șterge">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Rich editor for current language */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-white/40 font-medium uppercase tracking-wide">
                    {activeTab === 'ro' ? '🇷🇴 Text Română' : '🇬🇧 Text English'}
                  </label>
                  <span className="text-xs text-white/20">
                    {(activeTab === 'ro' ? s.roText : s.enText).replace(/<[^>]*>/g, '').length} caractere
                  </span>
                </div>
                <RichEditor
                  key={`${s.id}-${activeTab}-${editorKeys[s.id] || 0}`}
                  value={activeTab === 'ro' ? s.roText : s.enText}
                  onChange={v => updateSection(s.id, activeTab === 'ro' ? 'roText' : 'enText', v)}
                  onInsertImageFromLibrary={() => { setMediaSectionId(s.id); setShowMedia(true); }}
                  minHeight={80}
                  placeholder={activeTab === 'ro' ? 'Text în română...' : 'Text in English...'}
                />
              </div>

              {/* Quick peek other language */}
              {(activeTab === 'ro' ? s.enText : s.roText) && (
                <details>
                  <summary className="text-xs text-white/25 hover:text-white/50 cursor-pointer select-none transition-colors">
                    {activeTab === 'ro' ? '🇬🇧 Varianta EN' : '🇷🇴 Varianta RO'} (click pentru preview)
                  </summary>
                  <div className="mt-1.5 px-3 py-2 rounded-lg bg-white/3 text-xs text-white/35 italic leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: activeTab === 'ro' ? s.enText : s.roText }} />
                </details>
              )}

              {/* Image URL + picker */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-white/30">Imagine asociată secțiunii (opțional)</label>
                  <button onClick={() => { setMediaSectionId(s.id); setShowMedia(true); }}
                    className="text-xs text-[#4ab8ff] hover:underline">📁 Librărie</button>
                </div>
                <input value={s.imageUrl || ''} onChange={e => updateSection(s.id, 'imageUrl', e.target.value)}
                  className="w-full bg-[#0a0c12] border border-white/10 rounded-lg px-3 py-2 text-xs text-white/60 placeholder-white/15 focus:outline-none focus:border-[#295CA8] transition-colors"
                  placeholder="https://... sau selectează din librărie" />
                {s.imageUrl && (
                  <img src={s.imageUrl} alt="" className="mt-2 max-h-20 rounded-lg object-cover opacity-60"
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}
              </div>
            </div>
          </div>
        ))}

        {page.sections.length === 0 && (
          <div className="py-12 text-center text-white/30 text-sm border border-dashed border-white/10 rounded-xl">
            <p>Nicio secțiune definită. Adaugă prima secțiune sau apasă „Reset implicit".</p>
          </div>
        )}

        <button onClick={addSection}
          className="flex items-center gap-2 w-full justify-center py-3 border border-dashed border-white/15 rounded-xl text-sm text-white/35 hover:text-white hover:border-white/35 transition-all">
          <Plus size={15} /> Adaugă secțiune nouă
        </button>
      </div>

      <div className="flex justify-end gap-3 pt-2 border-t border-white/5">
        <button onClick={() => navigate('/pages')} className="px-4 py-2 rounded-lg text-sm text-white/40 hover:text-white border border-white/10 hover:border-white/20 transition-all">← Înapoi</button>
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
          <Save size={15} /> {saving ? 'Se salvează...' : 'Salvează modificările'}
        </button>
      </div>

      {showMedia && <MediaPickerModal onSelect={handleMediaSelect} onClose={() => setShowMedia(false)} />}
    </div>
  );
}
